import { useEffect, memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { loadDocketPageData } from 'redux/slices/docketSlice';
import { selectSelectedTeamMemberId } from 'redux/slices/selectedMemberSlice';
import { useAppSelector } from 'redux/store-hooks';
import AlertWrapper from 'toolkit/alerts/alert-wrapper';
import { DocketSigmaEmbed } from '../../embeds/docketSigmaEmbed';
import { useDocketDateRange } from '../docket-day-hook';
import DocketEvents from './DocketEvents';
import ActivityFeed from './ActivityFeed';
import { FlexContainer } from '@act/storybook';
import { useMobile } from '@act/utilities/mobile-context';

const DocketContent = memo(() => {
  const guideId = useAppSelector(selectSelectedTeamMemberId);
  const { isMobile } = useMobile();
  const { startEpochTime, endEpochTime, isToday, isPast } =
    useDocketDateRange();

  const dispatch = useDispatch();

  const loadData = useCallback(() => {
    if (!guideId || guideId === 'none') {
      return;
    }
    dispatch(loadDocketPageData({ guideId, startEpochTime, endEpochTime }));
  }, [dispatch, guideId, startEpochTime, endEpochTime]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  let content;

  if (isMobile) {
    content = (
      <FlexContainer
        direction="column"
        gap={2}
        width="100%"
        sx={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          overflowY: 'visible',
          '& > *': {
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          },
        }}
      >
        {!isPast && <DocketSigmaEmbed />}
        {!isPast && <DocketEvents guideId={guideId} />}
        <ActivityFeed guideId={guideId} isPast={isPast} isToday={isToday} />
      </FlexContainer>
    );
  } else {
    content = (
      <FlexContainer
        direction={'row'}
        gap={2}
        height="100%"
        width="100%"
        justifyContent="center"
        overflow="hidden"
      >
        {!isPast && (
          <FlexContainer
            direction="column"
            gap={2}
            height="100%"
            flexGrow={2}
            width="0"
            overflow="auto"
          >
            {isToday && <DocketSigmaEmbed />}
            <DocketEvents guideId={guideId} />
          </FlexContainer>
        )}
        <FlexContainer
          direction="column"
          height="100%"
          flexGrow={isPast ? undefined : 1}
          width={isPast ? '50%' : '0'}
        >
          <ActivityFeed guideId={guideId} isPast={isPast} isToday={isToday} />
        </FlexContainer>
      </FlexContainer>
    );
  }

  return (
    <FlexContainer
      direction="column"
      gap={2}
      height={isMobile ? 'auto' : '100%'}
      width="100%"
      overflow={isMobile ? 'visible' : 'hidden'}
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <AlertWrapper mode="global" />
      {content}
    </FlexContainer>
  );
});

DocketContent.displayName = 'DocketContent';

export default DocketContent;
